import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import calendarService from '../../../services/calendarService';
import { format, addDays } from 'date-fns';

const SchedulingAssistant = ({ onEventCreated, userId }) => {
  const [assistantStep, setAssistantStep] = useState('type'); // type, duration, date, time, details, confirm
  const [eventConfig, setEventConfig] = useState({
    event_type: 'call',
    duration: 30,
    preferred_date: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
    preferred_times: [],
    title: '',
    description: '',
    contact_info: '',
    auto_schedule: false
  });
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (assistantStep === 'time' && eventConfig?.preferred_date) {
      loadAvailableSlots();
    }
  }, [assistantStep, eventConfig?.preferred_date, eventConfig?.duration]);

  const loadAvailableSlots = async () => {
    if (!userId || !eventConfig?.preferred_date) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: fetchError } = await calendarService?.getAvailableTimeSlots(
        userId,
        eventConfig?.preferred_date,
        eventConfig?.duration
      );
      
      if (fetchError) {
        setError(fetchError);
        return;
      }
      
      setAvailableSlots(data || []);
    } catch (err) {
      setError('Failed to load available time slots');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    const steps = ['type', 'duration', 'date', 'time', 'details', 'confirm'];
    const currentIndex = steps?.indexOf(assistantStep);
    if (currentIndex < steps?.length - 1) {
      setAssistantStep(steps?.[currentIndex + 1]);
    }
  };

  const handleBack = () => {
    const steps = ['type', 'duration', 'date', 'time', 'details', 'confirm'];
    const currentIndex = steps?.indexOf(assistantStep);
    if (currentIndex > 0) {
      setAssistantStep(steps?.[currentIndex - 1]);
    }
  };

  const handleScheduleEvent = async () => {
    if (!selectedSlot) {
      setError('Please select a time slot');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const endTime = new Date(selectedSlot.getTime() + eventConfig?.duration * 60000);
      
      const eventData = {
        title: eventConfig?.title || `${eventConfig?.event_type?.replace('_', ' ')} Meeting`,
        description: eventConfig?.description,
        event_type: eventConfig?.event_type,
        start_time: selectedSlot?.toISOString(),
        end_time: endTime?.toISOString(),
        status: 'scheduled',
        reminder_minutes: 15,
        user_id: userId
      };

      const { data, error: createError } = await calendarService?.createCalendarEvent(eventData);

      if (createError) {
        setError(createError);
        return;
      }

      // Reset assistant
      setAssistantStep('type');
      setEventConfig({
        event_type: 'call',
        duration: 30,
        preferred_date: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
        preferred_times: [],
        title: '',
        description: '',
        contact_info: '',
        auto_schedule: false
      });
      setSelectedSlot(null);
      
      onEventCreated?.();
    } catch (err) {
      setError('Failed to schedule event');
    } finally {
      setLoading(false);
    }
  };

  const eventTypes = [
    { value: 'call', label: 'Phone Call', icon: 'Phone', description: 'Schedule a phone conversation' },
    { value: 'meeting', label: 'Meeting', icon: 'Users', description: 'Schedule an in-person or video meeting' },
    { value: 'follow_up', label: 'Follow-up', icon: 'ArrowRight', description: 'Schedule a follow-up conversation' },
    { value: 'reminder', label: 'Reminder', icon: 'Bell', description: 'Set a reminder for yourself' }
  ];

  const durationOptions = [
    { value: 15, label: '15 minutes' },
    { value: 30, label: '30 minutes' },
    { value: 45, label: '45 minutes' },
    { value: 60, label: '1 hour' },
    { value: 90, label: '1.5 hours' },
    { value: 120, label: '2 hours' }
  ];

  const renderStepContent = () => {
    switch (assistantStep) {
      case 'type':
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <Icon name="Bot" size={48} className="mx-auto mb-4 text-primary" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                What type of event would you like to schedule?
              </h3>
              <p className="text-muted-foreground">
                Choose the type of event that best fits your needs
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {eventTypes?.map(type => (
                <div
                  key={type?.value}
                  onClick={() => setEventConfig(prev => ({ ...prev, event_type: type?.value }))}
                  className={`
                    p-4 border rounded-lg cursor-pointer transition-colors
                    ${eventConfig?.event_type === type?.value
                      ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                    }
                  `}
                >
                  <div className="flex items-start space-x-3">
                    <Icon name={type?.icon} size={24} className="text-primary mt-1" />
                    <div>
                      <h4 className="font-medium text-foreground">{type?.label}</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        {type?.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'duration':
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <Icon name="Clock" size={48} className="mx-auto mb-4 text-primary" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                How long should this event be?
              </h3>
              <p className="text-muted-foreground">
                Select the duration that works best for your event
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {durationOptions?.map(option => (
                <button
                  key={option?.value}
                  onClick={() => setEventConfig(prev => ({ ...prev, duration: option?.value }))}
                  className={`
                    p-4 border rounded-lg transition-colors
                    ${eventConfig?.duration === option?.value
                      ? 'border-primary bg-primary/5 text-primary' :'border-border hover:border-primary/50 text-foreground'
                    }
                  `}
                >
                  {option?.label}
                </button>
              ))}
            </div>
          </div>
        );

      case 'date':
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <Icon name="Calendar" size={48} className="mx-auto mb-4 text-primary" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                When would you like to schedule this?
              </h3>
              <p className="text-muted-foreground">
                Choose your preferred date
              </p>
            </div>
            <div className="max-w-sm mx-auto">
              <Input
                type="date"
                value={eventConfig?.preferred_date}
                onChange={(e) => setEventConfig(prev => ({ ...prev, preferred_date: e?.target?.value }))}
                min={format(new Date(), 'yyyy-MM-dd')}
              />
            </div>
          </div>
        );

      case 'time':
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <Icon name="Clock" size={48} className="mx-auto mb-4 text-primary" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Available Time Slots
              </h3>
              <p className="text-muted-foreground">
                Choose from available times on {format(new Date(eventConfig?.preferred_date), 'MMMM dd, yyyy')}
              </p>
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Icon name="Loader2" size={24} className="animate-spin text-primary" />
              </div>
            ) : availableSlots?.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No available time slots for this date.</p>
                <Button
                  variant="outline"
                  onClick={handleBack}
                  className="mt-4"
                >
                  Choose Different Date
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-64 overflow-y-auto">
                {availableSlots?.map((slot, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedSlot(slot)}
                    className={`
                      p-3 border rounded-lg transition-colors
                      ${selectedSlot?.getTime() === slot?.getTime()
                        ? 'border-primary bg-primary/5 text-primary' :'border-border hover:border-primary/50 text-foreground'
                      }
                    `}
                  >
                    {format(slot, 'HH:mm')}
                  </button>
                ))}
              </div>
            )}
          </div>
        );

      case 'details':
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <Icon name="FileText" size={48} className="mx-auto mb-4 text-primary" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Event Details
              </h3>
              <p className="text-muted-foreground">
                Add any additional information for your event
              </p>
            </div>
            <div className="space-y-4 max-w-lg mx-auto">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Event Title
                </label>
                <Input
                  type="text"
                  value={eventConfig?.title}
                  onChange={(e) => setEventConfig(prev => ({ ...prev, title: e?.target?.value }))}
                  placeholder={`${eventConfig?.event_type?.replace('_', ' ')} Meeting`}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={eventConfig?.description}
                  onChange={(e) => setEventConfig(prev => ({ ...prev, description: e?.target?.value }))}
                  placeholder="Add any notes or agenda items"
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground resize-none"
                />
              </div>
            </div>
          </div>
        );

      case 'confirm':
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <Icon name="CheckCircle" size={48} className="mx-auto mb-4 text-success" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Confirm Your Event
              </h3>
              <p className="text-muted-foreground">
                Please review the details before scheduling
              </p>
            </div>
            
            <div className="bg-muted rounded-lg p-6 max-w-lg mx-auto">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Type:</span>
                  <span className="text-foreground font-medium capitalize">
                    {eventConfig?.event_type?.replace('_', ' ')}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Duration:</span>
                  <span className="text-foreground font-medium">
                    {eventConfig?.duration} minutes
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date:</span>
                  <span className="text-foreground font-medium">
                    {format(new Date(eventConfig?.preferred_date), 'MMMM dd, yyyy')}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Time:</span>
                  <span className="text-foreground font-medium">
                    {selectedSlot ? format(selectedSlot, 'HH:mm') : 'Not selected'}
                  </span>
                </div>
                
                {eventConfig?.title && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Title:</span>
                    <span className="text-foreground font-medium">
                      {eventConfig?.title}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (assistantStep) {
      case 'type': return eventConfig?.event_type;
      case 'duration': return eventConfig?.duration;
      case 'date': return eventConfig?.preferred_date;
      case 'time': return selectedSlot;
      case 'details': return true; // Optional step
      case 'confirm': return selectedSlot;
      default: return false;
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-8">
      {error && (
        <div className="mb-6 bg-destructive/10 border border-destructive/20 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={16} className="text-destructive" />
            <span className="text-destructive text-sm">{error}</span>
          </div>
        </div>
      )}
      {/* Progress Indicator */}
      <div className="flex items-center justify-center mb-8">
        <div className="flex items-center space-x-2">
          {['type', 'duration', 'date', 'time', 'details', 'confirm']?.map((step, index) => {
            const steps = ['type', 'duration', 'date', 'time', 'details', 'confirm'];
            const currentIndex = steps?.indexOf(assistantStep);
            const isActive = index === currentIndex;
            const isCompleted = index < currentIndex;
            
            return (
              <React.Fragment key={step}>
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                  ${isActive ? 'bg-primary text-primary-foreground' :
                    isCompleted ? 'bg-success text-success-foreground': 'bg-muted text-muted-foreground'}
                `}>
                  {isCompleted ? (
                    <Icon name="Check" size={16} />
                  ) : (
                    index + 1
                  )}
                </div>
                {index < steps?.length - 1 && (
                  <div className={`w-8 h-0.5 ${isCompleted ? 'bg-success' : 'bg-muted'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
      {/* Step Content */}
      <div className="min-h-64">
        {renderStepContent()}
      </div>
      {/* Navigation */}
      <div className="flex items-center justify-between mt-8">
        <Button
          variant="outline"
          onClick={handleBack}
          disabled={assistantStep === 'type'}
          iconName="ChevronLeft"
          iconPosition="left"
        >
          Back
        </Button>

        {assistantStep === 'confirm' ? (
          <Button
            onClick={handleScheduleEvent}
            disabled={loading || !canProceed()}
            iconName={loading ? "Loader2" : "Calendar"}
            iconPosition="left"
            className={loading ? "animate-spin" : ""}
          >
            {loading ? 'Scheduling...' : 'Schedule Event'}
          </Button>
        ) : (
          <Button
            onClick={handleNext}
            disabled={!canProceed()}
            iconName="ChevronRight"
            iconPosition="right"
          >
            Next
          </Button>
        )}
      </div>
    </div>
  );
};

export default SchedulingAssistant;